import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Wrench, RefreshCw, Hammer, Trash2, MessageCircle, ArrowRight, ArrowLeft, CalendarDays,
  CheckCircle2, Circle, Plus, Minus, X,
} from "lucide-react";
import config from "../../config";
import { useCityPrice } from "../../hooks/useCityPrice";
import { DEFAULT_HERO_IMAGE } from "../../config/serviceImages";
import { isUserLoggedIn } from "../common/Header";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
const STORAGE_KEY   = "cartItems";
const DAYS_AHEAD     = 3;

const WORK_TYPE_ICONS = { repair: Wrench, replace: RefreshCw, install: Hammer, remove: Trash2, consultation: MessageCircle };

const NOTE_EXAMPLES = [
  "I need a chandelier for my living room.",
  "I don't know which light to buy.",
  "I want 6 lights installed.",
  "I need a luxury light for a high ceiling.",
];

const fetchSlotAvailability = async (serviceId, duration) => {
  const res  = await fetch(`${API_BASE_URL}/api/booking/slot-availability`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ serviceId: String(serviceId), duration, daysAhead: DAYS_AHEAD }),
  });
  const data = await res.json();
  if (!res.ok || !data?.success) throw new Error(data?.message || "Unable to load slot availability.");
  return Array.isArray(data?.days) ? data.days : [];
};

const isSlotSelectable = (slot) =>
  Boolean(slot?.availability) && Number(slot?.seats || 0) - Number(slot?.booked || 0) > 0;

const getSlotState = (slot) => {
  const seats  = Number(slot?.seats || 0);
  const booked = Number(slot?.booked || 0);
  if (booked >= seats) return "booked";
  if (!slot?.availability) return "unavailable";
  return "available";
};

const formatDateLabel = (date) => {
  const [dd, mm, yyyy] = String(date || "").split("-");
  if (!dd) return date;
  const d = new Date(`${yyyy}-${mm}-${dd}T00:00:00`);
  if (isNaN(d.getTime())) return date;
  return d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });
};

const STEPS = [
  { key: "service",  label: "Service",  sub: "Repair, replace, install, remove or consult" },
  { key: "type",     label: "Light Type", sub: "Type, specific light & quantity"           },
  { key: "schedule", label: "Date & Time", sub: "Book your visit"                          },
];
const TYPE_SUBSTEPS = ["category", "specific", "note"];

const LightBookingPage = () => {
  const navigate = useNavigate();
  const { cityPrice } = useCityPrice();

  const service = useMemo(
    () => config.SERVICE_CONFIG?.technical?.services?.["electrical-services"]?.types?.find((t) => t.value === "light"),
    []
  );

  const [step, setStep] = useState("service");
  const [workTypeKey, setWorkTypeKey] = useState(null);
  const [category, setCategory] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]); // [{value,label,labour,material,quantity,image,startingPrice}]
  const [note, setNote] = useState("");
  const [hasAskedNote, setHasAskedNote] = useState(false);
  const [consultationOnly, setConsultationOnly] = useState(false);

  const [days, setDays]                     = useState([]);
  const [loadingAvailability, setLoading]   = useState(false);
  const [availabilityError, setAvailError]  = useState("");
  const [selectedDate, setSelectedDate]     = useState("");
  const [selectedTime, setSelectedTime]     = useState("");
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const stepIndex = STEPS.findIndex((s) => s.key === (TYPE_SUBSTEPS.includes(step) ? "type" : step));
  const workType = service?.workTypes?.find((wt) => wt.key === workTypeKey) || null;

  const cityBasePrice = (item) => cityPrice(item.value, item.price);
  const totalQty = selectedItems.reduce((sum, i) => sum + i.quantity, 0);
  const labourSubtotal = selectedItems.reduce((sum, i) => sum + i.labour * i.quantity, 0);
  const materialSubtotal = selectedItems.reduce((sum, i) => sum + i.material * i.quantity, 0);
  const subtotal = consultationOnly
    ? (service ? cityPrice(service.consultation.value, service.consultation.price) : 0)
    : labourSubtotal + materialSubtotal;
  const tax = Number(((subtotal * 18) / 100).toFixed(2));
  const surcharge = Number(((subtotal * 5) / 100).toFixed(2));
  const grandTotal = Number((subtotal + tax + surcharge).toFixed(2));
  const hasLuxuryItem = selectedItems.some((i) => i.startingPrice);

  const chooseWorkType = (key) => {
    setWorkTypeKey(key);
    setCategory(null);
    setStep("category");
  };

  const chooseCategory = (cat) => {
    setCategory(cat);
    if (workTypeKey === "consultation" || (cat.types || []).length === 0) {
      setConsultationOnly(true);
      setNote("");
      setStep("note");
    } else {
      setStep("specific");
    }
  };

  const getItemQty = (value) => selectedItems.find((i) => i.value === value)?.quantity || 0;

  const buildLabourPrice = (leaf) => Math.round(cityBasePrice(leaf) * (workType?.multiplier || 1));
  // Install/Replace involve swapping in a fixture — a placeholder material
  // cost is added on top; pure Repair/Remove work stays labour-only.
  const buildMaterialPrice = (leaf) =>
    workTypeKey === "install" || workTypeKey === "replace" ? Math.round(cityBasePrice(leaf) * 0.6) : 0;

  const addItem = (leaf) => {
    const composedValue = `light_${workTypeKey}_${leaf.value}`;
    const exists = selectedItems.find((i) => i.value === composedValue);
    if (exists) {
      setSelectedItems((prev) =>
        prev.map((i) => (i.value === composedValue ? { ...i, quantity: Math.min(i.quantity + 1, leaf.limit || 20) } : i))
      );
    } else {
      setSelectedItems((prev) => [
        ...prev,
        {
          value: composedValue,
          label: `${workType.label} — ${category.label} — ${leaf.label}`,
          labour: buildLabourPrice(leaf),
          material: buildMaterialPrice(leaf),
          quantity: 1,
          image: leaf.image,
          startingPrice: !!leaf.startingPrice,
          limit: leaf.limit || 20,
        },
      ]);
    }
  };

  const updateItemQty = (value, delta, limit) => {
    setSelectedItems((prev) =>
      prev
        .map((i) => (i.value === value ? { ...i, quantity: Math.max(0, Math.min(i.quantity + delta, limit || 20)) } : i))
        .filter((i) => i.quantity > 0)
    );
  };

  const removeItem = (value) => setSelectedItems((prev) => prev.filter((i) => i.value !== value));

  const addAnother = () => { setCategory(null); setStep("category"); };

  const confirmSelection = () => {
    if (selectedItems.length === 0) return;
    if (!hasAskedNote) { setNote(""); setStep("note"); } else { goToSchedule(); }
  };

  const submitNote = () => {
    setHasAskedNote(true);
    goToSchedule();
  };

  const goBack = () => {
    if (step === "note") {
      if (consultationOnly) { setStep("category"); }
      else { setStep("specific"); }
      return;
    }
    if (step === "specific") { setStep("category"); return; }
    if (step === "category") { setWorkTypeKey(null); setCategory(null); setStep("service"); return; }
    if (step === "schedule") { setStep(consultationOnly ? "note" : "specific"); return; }
    navigate("/schedule-booking/technical");
  };

  const goToSchedule = async () => {
    setStep("schedule");
    setSelectedDate(""); setSelectedTime(""); setSelectedSlotId("");
    setDays([]); setAvailError(""); setLoading(true);
    try {
      const fetched = await fetchSlotAvailability("technical", 1);
      setDays(fetched);
      const first = fetched.find((d) => d.slots.some(isSlotSelectable))?.date || fetched[0]?.date || "";
      setSelectedDate(first);
    } catch (e) {
      setAvailError(e?.message || "Failed to load available slots.");
    } finally {
      setLoading(false);
    }
  };

  const pickDate = (date) => { setSelectedDate(date); setSelectedTime(""); setSelectedSlotId(""); };
  const pickTime = (time, slotId) => { setSelectedTime(time); setSelectedSlotId(slotId || ""); };
  const activeDay = days.find((d) => d.date === selectedDate) || null;

  const handleCheckout = async () => {
    if (!selectedDate || !selectedTime) return;
    setCheckoutLoading(true);

    try {
      let cartItems;
      if (consultationOnly) {
        const c = service.consultation;
        const labelSuffix = category ? ` — ${category.label}` : "";
        cartItems = [{
          id: `light-consultation-${category?.key || "general"}-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
          title: `Light Consultation${labelSuffix}`,
          name: `Light Consultation${labelSuffix}`,
          short: note.trim() || "No additional notes",
          subtitle: "1 visit",
          category: "Light",
          price: cityPrice(c.value, c.price),
          image: category?.image || service?.image || DEFAULT_HERO_IMAGE,
          qty: 1,
        }];
      } else {
        cartItems = selectedItems.map((item) => ({
          id: `light-${item.value}-${selectedDate}-${selectedTime}`.replace(/\s+/g, ""),
          title: item.label,
          name: item.label,
          short: [
            `${item.quantity} unit${item.quantity > 1 ? "s" : ""}`,
            item.startingPrice ? "Starting price — technician confirms final amount on-site" : null,
            note.trim() || null,
          ].filter(Boolean).join(" · "),
          subtitle: `${item.quantity} unit${item.quantity > 1 ? "s" : ""}`,
          category: "Light",
          price: item.labour + item.material,
          image: item.image || service?.image || DEFAULT_HERO_IMAGE,
          qty: item.quantity,
        }));
      }

      const finalCartItems = cartItems.map((c) => ({
        ...c,
        bookingDate: selectedDate,
        bookingTime: selectedTime,
        bookingSlotId: selectedSlotId,
        schedule_booking: true,
        schedule_category_key: "technical",
      }));

      try {
        const existing = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
        const newIds = new Set(finalCartItems.map((i) => i.id));
        const withoutSame = Array.isArray(existing) ? existing.filter((i) => !newIds.has(i.id)) : [];
        localStorage.setItem(STORAGE_KEY, JSON.stringify([...withoutSame, ...finalCartItems]));
        window.dispatchEvent(new Event("cart-updated"));
      } catch { /* localStorage unavailable — navigation state below still works */ }

      // Back to the shared technical-services cart-building page (already
      // written to the shared cart above) instead of straight to checkout,
      // so other services can still be added before paying once for all.
      if (isUserLoggedIn()) {
        navigate("/schedule-booking/technical");
      } else {
        navigate("/user/user-login", { state: { redirectTo: "/schedule-booking/technical" } });
      }
    } finally {
      setCheckoutLoading(false);
    }
  };

  if (!service) {
    return <div style={S.page}><div style={S.container}><div style={S.errorBox}>Light service is not configured.</div></div></div>;
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <div style={S.breadcrumb}>
          <Link to="/" style={S.breadcrumbLink}>Home</Link> <span>&gt;</span>{" "}
          <Link to="/schedule-booking/technical" style={S.breadcrumbLink}>Technical Services</Link> <span>&gt;</span>{" "}
          <span>Light</span>
        </div>

        <div style={S.hero}>
          <div style={S.heroBadge}><CalendarDays size={13} /> LIGHT BOOKING</div>
          <h1 style={S.heroTitle}>Book Light Repair, Replace, Install, Remove or Consultation</h1>
        </div>

        <div style={S.stepRow}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <div style={S.stepItem}>
                <div style={{ ...S.stepCircle, ...(i === stepIndex ? S.stepCircleActive : {}), ...(i < stepIndex ? S.stepCircleDone : {}) }}>
                  {i < stepIndex ? <CheckCircle2 size={14} /> : i + 1}
                </div>
                <div>
                  <div style={{ ...S.stepLabel, ...(i <= stepIndex ? S.stepLabelActive : {}) }}>{s.label}</div>
                  <div style={S.stepSub}>{s.sub}</div>
                </div>
              </div>
              {i < STEPS.length - 1 && <div style={{ ...S.stepConnector, ...(i < stepIndex ? S.stepConnectorActive : {}) }} />}
            </React.Fragment>
          ))}
        </div>

        <button type="button" style={S.backBtn} onClick={goBack}>
          <ArrowLeft size={15} /> Back
        </button>

        {/* ── What do you need? (work type) ───────────────────────────────── */}
        {step === "service" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>What do you need?</h2>
            <p style={S.sectionSub}>Choose the service for your light</p>
            <div style={S.workTypeGrid}>
              {[...service.workTypes, { key: "consultation", label: "Consultation" }].map((wt) => {
                const Icon = WORK_TYPE_ICONS[wt.key] || Wrench;
                return (
                  <button key={wt.key} type="button" style={S.workTypeCard} onClick={() => chooseWorkType(wt.key)}>
                    <div style={S.workTypeIconWrap}><Icon size={22} color="#16a34a" /></div>
                    <div style={S.workTypeLabel}>{wt.label}</div>
                    <ArrowRight size={16} color="#9ca3af" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* ── Select Light Type ─────────────────────────────────────────────── */}
        {step === "category" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>Select Light Type — {workType?.label || "Consultation"}</h2>
            <p style={S.sectionSub}>Just tell us what light you need — the electrician handles the technical side</p>
            <div style={S.categoryGrid}>
              {service.subCategories.map((cat) => (
                <button key={cat.key} type="button" style={S.categoryCard} onClick={() => chooseCategory(cat)}>
                  <img
                    src={cat.types[0]?.image || DEFAULT_HERO_IMAGE}
                    alt={cat.label}
                    style={S.categoryImg}
                    onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                  />
                  <div style={S.categoryBody}>
                    <div style={S.categoryLabel}>{cat.label}</div>
                    {cat.types.length > 0 && <div style={S.categoryCount}>{cat.types.length} types</div>}
                  </div>
                </button>
              ))}
            </div>

            {selectedItems.length > 0 && (
              <div style={S.addedBox}>
                <div style={S.addedTitle}>Added to this booking</div>
                {selectedItems.map((item) => (
                  <div key={item.value} style={S.addedRow}>
                    <span>{item.label} × {item.quantity}</span>
                    <span style={S.addedRowActions}>
                      <span>₹{((item.labour + item.material) * item.quantity).toFixed(2)}</span>
                      <button type="button" style={S.addedRemoveBtn} onClick={() => removeItem(item.value)}>
                        <X size={13} />
                      </button>
                    </span>
                  </div>
                ))}
                <button type="button" style={S.continueBtn} onClick={confirmSelection}>
                  Continue ({totalQty} light{totalQty > 1 ? "s" : ""}) <ArrowRight size={16} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* ── Select Specific Light + Quantity ─────────────────────────────── */}
        {step === "specific" && category && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>{category.label} — Select Specific Light</h2>
            <p style={S.sectionSub}>Pick the exact light and how many</p>

            <div style={S.typeGrid}>
              {category.types.map((leaf) => {
                const composedValue = `light_${workTypeKey}_${leaf.value}`;
                const qty = getItemQty(composedValue);
                const reachedMax = qty >= (leaf.limit || 20);
                return (
                  <div key={leaf.value} style={{ ...S.typeCard, ...(qty > 0 ? S.typeCardActive : {}) }}>
                    <img
                      src={leaf.image || DEFAULT_HERO_IMAGE}
                      alt={leaf.label}
                      style={S.typeImg}
                      onError={(e) => { e.currentTarget.src = DEFAULT_HERO_IMAGE; }}
                    />
                    <div style={S.typeBody}>
                      <div style={S.typeCheckRow}>
                        {qty > 0 ? <CheckCircle2 size={16} color="#16a34a" /> : <Circle size={16} color="#d1d5db" />}
                        <span style={S.typeName}>{leaf.label}</span>
                      </div>
                      <div style={S.typePrice}>
                        {leaf.startingPrice ? "Starting " : ""}₹{buildLabourPrice(leaf) + buildMaterialPrice(leaf)}
                      </div>
                      <div style={S.qtyRow}>
                        <button
                          type="button"
                          style={qty === 0 ? S.qtyBtnDisabled : S.qtyBtn}
                          onClick={() => qty > 0 && updateItemQty(composedValue, -1, leaf.limit)}
                          disabled={qty === 0}
                        >
                          <Minus size={13} />
                        </button>
                        <span style={S.qtyText}>{qty}</span>
                        <button
                          type="button"
                          style={reachedMax ? S.qtyBtnDisabled : S.qtyBtnP}
                          onClick={() => addItem(leaf)}
                          disabled={reachedMax}
                        >
                          <Plus size={13} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {selectedItems.length > 0 && (
              <div style={S.addedBox}>
                <div style={S.addedTitle}>Added to this booking</div>
                {selectedItems.map((item) => (
                  <div key={item.value} style={S.addedRow}>
                    <span>{item.label} × {item.quantity}</span>
                    <span style={S.addedRowActions}>
                      <span>₹{((item.labour + item.material) * item.quantity).toFixed(2)}</span>
                      <button type="button" style={S.addedRemoveBtn} onClick={() => removeItem(item.value)}>
                        <X size={13} />
                      </button>
                    </span>
                  </div>
                ))}
              </div>
            )}

            <div style={S.typeFooterRow}>
              <button type="button" style={S.addAnotherBtn} onClick={addAnother}>
                <Plus size={15} /> Add Another
              </button>
              <button
                type="button"
                style={{ ...S.continueBtn, ...S.typeFooterBtn, ...(selectedItems.length === 0 ? S.continueBtnDisabled : {}) }}
                disabled={selectedItems.length === 0}
                onClick={confirmSelection}
              >
                Continue ({totalQty}) <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── Consultation (optional, asked once) ──────────────────────────── */}
        {step === "note" && (
          <div style={S.card}>
            <h2 style={S.sectionTitle}>💬 Need Help?</h2>
            <p style={S.sectionSub}>
              {hasLuxuryItem
                ? "Talk to a ClickNido Electrician — recommended for chandeliers and large/high-ceiling lights"
                : "Talk to a ClickNido Electrician — tell us what you need, or skip if you're all set"}
            </p>
            <textarea
              style={S.textarea}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={NOTE_EXAMPLES[0]}
              rows={4}
            />
            <div style={S.examplesBox}>
              <div style={S.examplesTitle}>Examples</div>
              {NOTE_EXAMPLES.map((ex) => (
                <button key={ex} type="button" style={S.exampleChip} onClick={() => setNote(ex)}>
                  {ex}
                </button>
              ))}
            </div>
            <div style={S.noteFooterRow}>
              <button type="button" style={S.skipBtn} onClick={submitNote}>
                Skip
              </button>
              <button
                type="button"
                style={{ ...S.continueBtn, ...S.noteFooterBtn, ...(consultationOnly && note.trim() === "" ? S.continueBtnDisabled : {}) }}
                disabled={consultationOnly && note.trim() === ""}
                onClick={submitNote}
              >
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* ── Date & Time (2-hour slot) ─────────────────────────────────────── */}
        {step === "schedule" && (
          <>
            <div style={S.summaryBar}>
              <div style={S.summaryBody}>
                <div style={S.summaryTitle}>Light Booking Summary</div>
                <div style={S.summaryChipRow}>
                  {consultationOnly ? (
                    <span style={S.summaryChip}>Light Consultation{category ? ` — ${category.label}` : ""}</span>
                  ) : (
                    selectedItems.map((item) => (
                      <span key={item.value} style={S.summaryChip}>{item.label} × {item.quantity}</span>
                    ))
                  )}
                  <span style={S.summaryDurationChip}>{hasLuxuryItem ? "Starting " : ""}₹{grandTotal.toFixed(2)}</span>
                </div>
                {note.trim() && <div style={S.noteBox}>"{note.trim()}"</div>}
                {hasLuxuryItem && (
                  <div style={S.noteBox}>
                    This includes a Starting Price for luxury/high-ceiling lighting — the electrician confirms the final amount after assessment, and gets your approval before proceeding.
                  </div>
                )}
              </div>
              <button type="button" style={S.summaryChangeBtn} onClick={goBack}>
                Change
              </button>
            </div>

            <div style={S.scheduleHead}>
              <h2 style={S.sectionTitle}>Choose Date &amp; Time</h2>
              <div style={S.legend}>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#22c55e" }} /> Available</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#ef4444" }} /> Booked</span>
                <span style={S.legendItem}><span style={{ ...S.legendDot, background: "#9ca3af" }} /> Unavailable</span>
              </div>
            </div>

            <div style={S.scheduleColumns}>
              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>1. Select Date</div>
                {loadingAvailability ? (
                  <div style={S.stateBox}>Loading…</div>
                ) : availabilityError ? (
                  <div style={S.errorBox}>{availabilityError}</div>
                ) : (
                  <div style={S.dateList}>
                    {days.map((day) => {
                      const hasOpen  = day.slots.some(isSlotSelectable);
                      const selected = selectedDate === day.date;
                      return (
                        <button
                          key={day.date} type="button"
                          disabled={!hasOpen}
                          style={{ ...S.dateRow, ...(selected ? S.dateRowActive : {}), ...(!hasOpen ? S.dateRowDisabled : {}) }}
                          onClick={() => hasOpen && pickDate(day.date)}
                        >
                          <CalendarDays size={16} color={selected ? "#fff" : hasOpen ? "#16a34a" : "#9ca3af"} />
                          <span style={S.dateRowLabel}>{formatDateLabel(day.date)}</span>
                          <span style={{ ...S.dateRowSub, ...(selected ? { color: "#bbf7d0" } : {}) }}>
                            {hasOpen ? `${day.slots.filter(isSlotSelectable).length} open` : "Full"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div style={S.scheduleCol}>
                <div style={S.scheduleColTitle}>2. Select Time Slot</div>
                <div style={S.slotGrid}>
                  {(activeDay?.slots || []).map((slot) => {
                    const state      = getSlotState(slot);
                    const selectable = state === "available";
                    const freeSeats  = Number(slot.seats || 0) - Number(slot.booked || 0);
                    const selected   = selectedTime === slot.time;
                    return (
                      <button
                        key={`${activeDay?.date}-${slot.time}`} type="button"
                        disabled={!selectable}
                        style={{
                          ...S.slotBtn,
                          ...(selected ? S.slotBtnActive : {}),
                          ...(state === "booked" ? S.slotBtnBooked : {}),
                          ...(state === "unavailable" ? S.slotBtnDisabled : {}),
                        }}
                        onClick={() => selectable && pickTime(slot.time, slot._id)}
                      >
                        <div style={S.slotTimeRow}>
                          <span style={{ ...S.slotTime, ...(selected ? { color: "#fff" } : {}) }}>{slot.time}</span>
                          {selected && <CheckCircle2 size={13} color="#fff" />}
                        </div>
                        <div style={{ ...S.slotMeta, ...(selected ? { color: "#bbf7d0" } : {}), ...(state === "booked" ? { color: "#dc2626", fontWeight: 700 } : {}) }}>
                          {state === "available" ? `${freeSeats} left` : state === "booked" ? "Booked" : "Unavailable"}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            <div style={S.breakdownBox}>
              <div style={S.breakdownRow}><span>Service / Labour</span><span>₹{labourSubtotal.toFixed(2)}</span></div>
              {materialSubtotal > 0 && <div style={S.breakdownRow}><span>Material</span><span>₹{materialSubtotal.toFixed(2)}</span></div>}
              <div style={S.breakdownRow}><span>Additional Work</span><span>₹0.00</span></div>
              <div style={S.breakdownRow}><span>Tax &amp; Surcharge</span><span>₹{(tax + surcharge).toFixed(2)}</span></div>
              <div style={S.breakdownGrandRow}><span>{hasLuxuryItem ? "Starting Price" : "Total"}</span><span>₹{grandTotal.toFixed(2)}</span></div>
            </div>

            <button
              type="button"
              style={{ ...S.continueBtn, ...(!selectedTime || checkoutLoading ? S.continueBtnDisabled : {}) }}
              disabled={!selectedTime || checkoutLoading}
              onClick={handleCheckout}
            >
              {checkoutLoading ? "Proceeding..." : isUserLoggedIn() ? "Proceed to Checkout" : "Login & Continue"} <ArrowRight size={16} />
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const S = {
  page:      { background: "#fafafa", minHeight: "100vh", padding: "28px 20px 60px", fontFamily: "Inter, ui-sans-serif, system-ui, sans-serif", color: "#111827" },
  container: { maxWidth: "1100px", margin: "0 auto" },
  breadcrumb:     { fontSize: "13px", color: "#9ca3af", marginBottom: "22px", display: "flex", gap: "6px", flexWrap: "wrap" },
  breadcrumbLink: { color: "#16a34a", fontWeight: 600, textDecoration: "none" },

  hero:      { textAlign: "center", maxWidth: "660px", margin: "0 auto 26px" },
  heroBadge: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#dcfce7", color: "#16a34a", fontSize: "11px", fontWeight: 800, padding: "6px 12px", borderRadius: "999px", marginBottom: "12px" },
  heroTitle: { margin: 0, fontSize: "22px", fontWeight: 800, letterSpacing: "-0.5px", color: "#111827" },

  stepRow: { display: "flex", alignItems: "flex-start", justifyContent: "center", gap: "4px", margin: "0 0 28px", flexWrap: "wrap" },
  stepItem: { display: "flex", alignItems: "center", gap: "8px" },
  stepCircle: { width: "30px", height: "30px", borderRadius: "50%", background: "#e5e7eb", color: "#6b7280", fontSize: "12px", fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  stepCircleActive: { background: "#16a34a", color: "#fff" },
  stepCircleDone: { background: "#bbf7d0", color: "#16a34a" },
  stepLabel: { fontSize: "13px", fontWeight: 700, color: "#9ca3af", lineHeight: 1.3 },
  stepLabelActive: { color: "#111827" },
  stepSub: { fontSize: "11px", color: "#9ca3af", marginTop: "1px" },
  stepConnector: { width: "34px", height: "2px", background: "#e5e7eb", marginTop: "15px", flexShrink: 0 },
  stepConnectorActive: { background: "#16a34a" },

  backBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "none", border: "none", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", marginBottom: "16px", padding: 0 },

  card: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "18px", padding: "22px", marginBottom: "24px" },
  sectionTitle: { margin: "0 0 4px", fontSize: "19px", fontWeight: 800, color: "#111827" },
  sectionSub: { margin: "0 0 16px", fontSize: "13px", color: "#6b7280" },

  workTypeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px" },
  workTypeCard: { display: "flex", alignItems: "center", gap: "12px", padding: "16px 18px", borderRadius: "14px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  workTypeIconWrap: { width: "42px", height: "42px", borderRadius: "12px", background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  workTypeLabel: { fontSize: "15px", fontWeight: 800, color: "#111827", flex: 1 },

  categoryGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "14px" },
  categoryCard: { background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "14px", overflow: "hidden", cursor: "pointer", padding: 0, textAlign: "left" },
  categoryImg: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", background: "#f0f2fa" },
  categoryBody: { padding: "10px 12px" },
  categoryLabel: { fontSize: "13.5px", fontWeight: 800, color: "#111827" },
  categoryCount: { fontSize: "11.5px", color: "#9ca3af", marginTop: "2px" },

  typeGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px", marginBottom: "18px" },
  typeCard: { background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: "14px", overflow: "hidden" },
  typeCardActive: { borderColor: "#16a34a", boxShadow: "0 0 0 3px rgba(22,163,74,0.12)" },
  typeImg: { width: "100%", aspectRatio: "1 / 1", objectFit: "cover", display: "block", background: "#f0f2fa" },
  typeBody: { padding: "10px 12px 12px" },
  typeCheckRow: { display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" },
  typeName: { fontSize: "13px", fontWeight: 800, color: "#111827" },
  typePrice: { fontSize: "13px", fontWeight: 800, color: "#16a34a", margin: "2px 0 8px" },

  qtyRow: { display: "flex", alignItems: "center", gap: "10px" },
  qtyBtn: { width: "26px", height: "26px", borderRadius: "50%", border: "1px solid #e4e7ef", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#6b7280", flexShrink: 0 },
  qtyBtnP: { width: "26px", height: "26px", borderRadius: "50%", border: "none", background: "#16a34a", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", flexShrink: 0 },
  qtyBtnDisabled: { width: "26px", height: "26px", borderRadius: "50%", border: "1px solid #eceef5", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", cursor: "not-allowed", color: "#c1c5cf", flexShrink: 0 },
  qtyText: { minWidth: "18px", textAlign: "center", fontSize: "13px", fontWeight: 700, color: "#111827" },

  textarea: { width: "100%", minHeight: "100px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", fontFamily: "inherit", fontSize: "13px", resize: "vertical", marginBottom: "14px", boxSizing: "border-box" },
  examplesBox: { marginBottom: "18px" },
  examplesTitle: { fontSize: "12px", fontWeight: 700, color: "#9ca3af", marginBottom: "8px" },
  exampleChip: { display: "block", width: "100%", textAlign: "left", background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "10px", padding: "8px 12px", fontSize: "12.5px", color: "#374151", cursor: "pointer", marginBottom: "6px" },
  noteFooterRow: { display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "12px" },
  skipBtn: { background: "none", border: "none", color: "#6b7280", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "10px 6px" },
  noteFooterBtn: { width: "auto", padding: "13px 24px", marginTop: 0 },

  addedBox: { background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "14px", padding: "14px 16px", marginBottom: "18px", marginTop: "18px" },
  addedTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  addedRow: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "13px", color: "#374151", padding: "5px 0" },
  addedRowActions: { display: "flex", alignItems: "center", gap: "10px", fontWeight: 700 },
  addedRemoveBtn: { width: "22px", height: "22px", borderRadius: "50%", border: "none", background: "#fee2e2", color: "#b91c1c", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" },

  typeFooterRow: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  addAnotherBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "10px 16px", borderRadius: "10px" },
  typeFooterBtn: { width: "auto", padding: "13px 24px", marginTop: 0 },

  summaryBar: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "16px 20px", marginBottom: "20px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "16px", flexWrap: "wrap" },
  summaryBody: { minWidth: 0, flex: 1 },
  summaryTitle: { fontSize: "15px", fontWeight: 800, color: "#111827", marginBottom: "8px" },
  summaryChipRow: { display: "flex", flexWrap: "wrap", gap: "6px" },
  summaryChip: { fontSize: "12px", fontWeight: 700, color: "#15803d", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryDurationChip: { fontSize: "12px", fontWeight: 700, color: "#fff", background: "#16a34a", padding: "4px 10px", borderRadius: "999px", whiteSpace: "nowrap" },
  summaryChangeBtn: { display: "inline-flex", alignItems: "center", gap: "6px", background: "#fff", border: "1.5px solid #16a34a", color: "#16a34a", fontWeight: 700, fontSize: "13px", cursor: "pointer", padding: "9px 16px", borderRadius: "10px", whiteSpace: "nowrap" },
  noteBox: { fontSize: "12px", color: "#6b7280", background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "10px", padding: "10px 12px", marginTop: "10px", lineHeight: 1.4 },

  scheduleHead: { display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "12px", marginBottom: "18px" },
  legend: { display: "flex", gap: "16px", flexWrap: "wrap" },
  legendItem: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: 600, color: "#6b7280" },
  legendDot: { width: "9px", height: "9px", borderRadius: "50%", display: "inline-block" },

  scheduleColumns: { display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "20px", marginBottom: "22px", alignItems: "start" },
  scheduleCol: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: "16px", padding: "18px" },
  scheduleColTitle: { fontSize: "13px", fontWeight: 800, color: "#111827", marginBottom: "14px" },

  dateList: { display: "flex", flexDirection: "column", gap: "8px" },
  dateRow: { display: "flex", alignItems: "center", gap: "10px", padding: "12px 14px", borderRadius: "12px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "left" },
  dateRowActive: { background: "#16a34a", borderColor: "#16a34a", color: "#fff" },
  dateRowDisabled: { opacity: 0.45, cursor: "not-allowed" },
  dateRowLabel: { flex: 1, fontSize: "13px", fontWeight: 700, color: "inherit" },
  dateRowSub: { fontSize: "11px", fontWeight: 700, color: "#16a34a" },

  slotGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(90px, 1fr))", gap: "8px" },
  slotBtn:  { padding: "9px 6px", borderRadius: "10px", border: "1.5px solid #e5e7eb", background: "#fff", cursor: "pointer", textAlign: "center" },
  slotBtnActive: { border: "1.5px solid #16a34a", background: "#16a34a" },
  slotBtnBooked: { border: "1.5px solid #fecaca", background: "#fef2f2", cursor: "not-allowed" },
  slotBtnDisabled: { opacity: 0.5, cursor: "not-allowed", background: "#f3f4f6" },
  slotTimeRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: "4px" },
  slotTime: { fontSize: "12px", fontWeight: 800, color: "#111827" },
  slotMeta: { fontSize: "10px", color: "#9ca3af", marginTop: "3px" },

  breakdownBox: { background: "#f8f9fc", border: "1px solid #eceef5", borderRadius: "14px", padding: "14px 16px", marginBottom: "16px" },
  breakdownRow: { display: "flex", justifyContent: "space-between", fontSize: "13px", color: "#374151", marginBottom: "6px" },
  breakdownGrandRow: { display: "flex", justifyContent: "space-between", fontSize: "16px", fontWeight: 800, color: "#111827", paddingTop: "6px", borderTop: "1px solid #eceef5" },

  stateBox: { padding: "20px", textAlign: "center", color: "#6b7280", fontSize: "13px" },
  errorBox: { padding: "14px", borderRadius: "10px", background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", fontSize: "13px" },

  continueBtn: { width: "100%", marginTop: "6px", padding: "15px 0", borderRadius: "12px", background: "#16a34a", color: "#fff", fontSize: "15px", fontWeight: 800, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" },
  continueBtnDisabled: { opacity: 0.45, cursor: "not-allowed" },
};

export default LightBookingPage;
